import pickBy from 'lodash/pickBy'
import {
  GeckoHorizontalWeld,
  GeckoHorizontalWeldJSON,
  GeckoPipeCircumferentialWeld,
  GeckoPipeCircumferentialWeldJSON,
  GeckoPipeIntegralSupport,
  GeckoPipeIntegralSupportJSON,
  GeckoPipeNonIntegralSupport,
  GeckoPipeNonIntegralSupportJSON,
  GeckoPipeSeamWeld,
  GeckoPipeSeamWeldJSON,
  GeckoPipeWrap,
  GeckoPipeWrapJSON,
  GeckoVerticalWeld,
  GeckoVerticalWeldJSON,
} from 'types'
import {
  GeckoComponentModelType,
  GeckoFeatureModelType,
} from 'types/assetModel/assetModelEnums'
import {
  compassOrientationFormField,
  installationDateFormField,
  tminFormField,
  wallThicknessFormField,
} from 'types/assetModel/commonFields'
import {
  GeckoEditableComponent,
  SceneConfig,
} from 'types/assetModel/components/geckoEditableComponent'
import {
  GeckoFixedSensor,
  GeckoFixedSensorJSON,
} from 'types/assetModel/features/geckoFixedSensor'
import {
  GeckoNozzle,
  GeckoNozzleJSON,
} from 'types/assetModel/features/geckoNozzle'
import {
  GeckoPlate,
  GeckoPlateJSON,
} from 'types/assetModel/features/geckoPlate'
import { weightThicknessValue } from 'types/assetModel/thicknessUnitHelpers'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  getNumberFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentRect } from 'types/geometric/geckoComponentRect'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { assetModelPrecision } from 'utils/constants'
import { forecastMath } from 'utils/fcMath'
import { parseDate, parseNumber, parseString } from 'utils/parsingHelpers'
import {
  optionalNumberSchema,
  optionalStringSchema,
  requiredNumberSchema,
  requiredStringSchema,
} from 'utils/zodHelpers'

const { pi } = forecastMath

import { GeckoPipeFlowDirection } from './geckoPipeSection'

type PipeElbowSupportedFeatures =
  | GeckoFixedSensorJSON
  | GeckoNozzleJSON
  | GeckoPipeNonIntegralSupportJSON
  | GeckoPipeIntegralSupportJSON
  | GeckoPipeCircumferentialWeldJSON
  | GeckoPipeSeamWeldJSON
  | GeckoPipeWrapJSON
  | GeckoPlateJSON
  | GeckoVerticalWeldJSON
  | GeckoHorizontalWeldJSON

export const PipeElbowAvailableFeatures: GeckoFeatureModelType[] = []

export type PipeElbowAssetModelJSON = {
  name: string
  type: GeckoComponentModelType.PipeElbow
  radius: number // Minor radius (pipe radius)
  major_radius: number // Major radius (elbow radius)
  angle: number
  material: string | undefined
  installation_date: string | undefined
  joint_efficiency: number | undefined
  stress_tolerance: number | undefined

  flow_direction: GeckoPipeFlowDirection | undefined
  clock_bearing_rotation: number | undefined
  features: Record<string, PipeElbowSupportedFeatures>
  scene_config: SceneConfig
  component_bounds: Record<string, number>
  nominal: {
    wall_thickness?: number
  }
  tmin: number | undefined
  compass_orientation?: number
}

export const NewPipeElbowAssetModelJSON = {
  name: 'New Pipe Elbow',
  type: GeckoComponentModelType.PipeElbow,
  radius: 0,
  major_radius: 0,
  angle: 90,
  material: undefined,
  installation_date: undefined,
  joint_efficiency: undefined,
  stress_tolerance: undefined,

  flow_direction: undefined,
  clock_bearing_rotation: 0,
  features: {},
  scene_config: {},
  component_bounds: {
    x_start: 0,
    y_start: 0,
    x_end: 0,
    y_end: 0,
  },
  tmin: undefined,
  compass_orientation: undefined,
  nominal: {},
} as PipeElbowAssetModelJSON

export class GeckoPipeElbow extends GeckoEditableComponent {
  type: GeckoComponentModelType.PipeElbow
  radius: GeckoMeasurement // Minor radius (pipe radius)
  majorRadius: GeckoMeasurement // Major radius (elbow radius)
  angle: number // Degrees
  material: string | undefined
  installationDate: Date | undefined
  jointEfficiency: number | undefined
  stressTolerance: GeckoMeasurement | undefined

  flowDirection: GeckoPipeFlowDirection | undefined
  clockBearingRotation: number | undefined
  componentBounds: GeckoComponentRect
  sceneConfig: SceneConfig
  nozzles: Map<string, GeckoNozzle>
  fixedSensors: Map<string, GeckoFixedSensor>
  plates: Map<string, GeckoPlate>
  verticalWelds: Map<string, GeckoVerticalWeld>
  horizontalWelds: Map<string, GeckoHorizontalWeld>
  pipeNonIntegralSupports: Map<string, GeckoPipeNonIntegralSupport>
  pipeIntegralSupports: Map<string, GeckoPipeIntegralSupport>
  pipeCircumferentialWelds: Map<string, GeckoPipeCircumferentialWeld>
  pipeSeamWelds: Map<string, GeckoPipeSeamWeld>
  pipeWraps: Map<string, GeckoPipeWrap>
  wallNominalThickness: GeckoMeasurement | undefined
  tmin: GeckoMeasurement | undefined
  compassOrientation: number | undefined

  private constructor(
    id: string,
    json: Record<string, unknown>,
    name: string,
    radius: GeckoMeasurement,
    majorRadius: GeckoMeasurement,
    angle: number,
    material: string | undefined,
    installationDate: Date | undefined,
    jointEfficiency: number | undefined,
    stressTolerance: GeckoMeasurement | undefined,
    flowDirection: GeckoPipeFlowDirection | undefined,
    clockBearingRotation: number | undefined,
    componentBounds: GeckoComponentRect,
    sceneConfig: SceneConfig,
    nozzles: Map<string, GeckoNozzle>,
    fixedSensors: Map<string, GeckoFixedSensor>,
    plates: Map<string, GeckoPlate>,
    verticalWelds: Map<string, GeckoVerticalWeld>,
    horizontalWelds: Map<string, GeckoHorizontalWeld>,
    pipeNonIntegralSupports: Map<string, GeckoPipeNonIntegralSupport>,
    pipeIntegralSupports: Map<string, GeckoPipeIntegralSupport>,
    pipeCircumferentialWelds: Map<string, GeckoPipeCircumferentialWeld>,
    pipeSeamWelds: Map<string, GeckoPipeSeamWeld>,
    pipeWraps: Map<string, GeckoPipeWrap>,
    wallNominalThickness: GeckoMeasurement | undefined,
    tmin: GeckoMeasurement | undefined,
    compassOrientation: number | undefined
  ) {
    super(id, name, json)
    this.type = GeckoComponentModelType.PipeElbow
    this.radius = radius
    this.majorRadius = majorRadius
    this.angle = angle
    this.material = material
    this.installationDate = installationDate
    this.jointEfficiency = jointEfficiency
    this.stressTolerance = stressTolerance
    this.flowDirection = flowDirection
    this.clockBearingRotation = clockBearingRotation
    this.componentBounds = componentBounds
    this.sceneConfig = sceneConfig
    this.nozzles = nozzles
    this.fixedSensors = fixedSensors
    this.plates = plates
    this.verticalWelds = verticalWelds
    this.horizontalWelds = horizontalWelds
    this.pipeNonIntegralSupports = pipeNonIntegralSupports
    this.pipeIntegralSupports = pipeIntegralSupports
    this.pipeCircumferentialWelds = pipeCircumferentialWelds
    this.pipeSeamWelds = pipeSeamWelds
    this.pipeWraps = pipeWraps
    this.wallNominalThickness = wallNominalThickness
    this.tmin = tmin
    this.compassOrientation = compassOrientation
  }

  public get nozzlesArray() {
    return Array.from(this.nozzles.values())
  }

  public get fixedSensorsArray() {
    return Array.from(this.fixedSensors.values())
  }

  public get platesArray() {
    return Array.from(this.plates.values())
  }

  public get verticalWeldsArray() {
    return Array.from(this.verticalWelds.values())
  }

  public get horizontalWeldsArray() {
    return Array.from(this.horizontalWelds.values())
  }

  public get pipeNonIntegralSupportsArray() {
    return Array.from(this.pipeNonIntegralSupports.values())
  }

  public get pipeIntegralSupportsArray() {
    return Array.from(this.pipeIntegralSupports.values())
  }

  public get pipeCircumferentialWeldsArray() {
    return Array.from(this.pipeCircumferentialWelds.values())
  }

  public get pipeSeamWeldsArray() {
    return Array.from(this.pipeSeamWelds.values())
  }

  public get pipeWrapsArray() {
    return Array.from(this.pipeWraps.values())
  }

  public static CreateFromJSON(id: string, json: PipeElbowAssetModelJSON) {
    const name = json.name as string
    const compassOrientation = json.compass_orientation
    const pipeRadius = GeckoMeasurement.CreateFromValue(
      json.radius as number,
      GeckoMeasurementUnit.Meters
    )
    const pipeMajorRadius = GeckoMeasurement.CreateFromValue(
      json.major_radius as number,
      GeckoMeasurementUnit.Meters
    )
    const pipeAngle = json.angle as number
    const material = parseString(json.material)
    const installationDate = parseDate(json.installation_date)

    const jointEfficiency = parseNumber(json.joint_efficiency)
    const parsedStressTolerance = parseNumber(json.stress_tolerance)
    const stressTolerance =
      parsedStressTolerance !== undefined
        ? GeckoMeasurement.CreateFromValue(
            parsedStressTolerance,
            GeckoMeasurementUnit.Megapascals
          )
        : undefined
    const flowDirection = json.flow_direction as
      | GeckoPipeFlowDirection
      | undefined

    const clockBearingRotation = parseNumber(json.clock_bearing_rotation)

    const componentBoundsData = json.component_bounds as Record<string, number>
    const componentBounds = GeckoComponentRect.CreateFromXYs(
      id,
      componentBoundsData.x_start,
      componentBoundsData.y_start,
      componentBoundsData.x_end,
      componentBoundsData.y_end,
      GeckoMeasurementUnit.Meters,
      GeckoMeasurementUnit.Meters
    )

    const sceneConfig = json.scene_config as SceneConfig

    const featureData = json.features as Record<
      string,
      PipeElbowSupportedFeatures
    >

    const childPlateJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.Plate
    })
    const plates = GeckoPlate.CreateMapFromJSON(
      id,
      childPlateJSON as Record<string, GeckoPlateJSON>
    )

    const childHorizontalWeldJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.HorizontalWeld
    })
    const horizontalWelds = GeckoHorizontalWeld.CreateMapFromJSON(
      id,
      childHorizontalWeldJSON as Record<string, GeckoHorizontalWeldJSON>
    )

    const childVerticalWeldJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.VerticalWeld
    })
    const verticalWelds = GeckoVerticalWeld.CreateMapFromJSON(
      id,
      childVerticalWeldJSON as Record<string, GeckoVerticalWeldJSON>
    )

    const childNozzleJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.Nozzle
    })
    const nozzles = GeckoNozzle.CreateMapFromJSON(
      id,
      childNozzleJSON as Record<string, GeckoNozzleJSON>
    )

    const childFixedSensorJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.FixedSensor
    })
    const fixedSensors = GeckoFixedSensor.CreateMapFromJSON(
      id,
      childFixedSensorJSON as Record<string, GeckoFixedSensorJSON>
    )

    const childPipeNonIntegralSupportJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.PipeNonIntegralSupport
    })

    const pipeNonIntegralSupports =
      GeckoPipeNonIntegralSupport.CreateMapFromJSON(
        id,
        childPipeNonIntegralSupportJSON as Record<
          string,
          GeckoPipeNonIntegralSupportJSON
        >
      )

    const childPipeIntegralSupportJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.PipeIntegralSupport
    })

    const pipeIntegralSupports = GeckoPipeIntegralSupport.CreateMapFromJSON(
      id,
      childPipeIntegralSupportJSON as Record<
        string,
        GeckoPipeIntegralSupportJSON
      >
    )

    const childPipeCircumferentialWeldJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.PipeCircumferentialWeld
    })

    const pipeCircumferentialWelds =
      GeckoPipeCircumferentialWeld.CreateMapFromJSON(
        id,
        childPipeCircumferentialWeldJSON as unknown as Record<
          string,
          GeckoPipeCircumferentialWeldJSON
        >,
        componentBounds
      )
    const childPipeSeamWeldJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.PipeSeamWeld
    })

    const pipeSeamWelds = GeckoPipeSeamWeld.CreateMapFromJSON(
      id,
      childPipeSeamWeldJSON as Record<string, GeckoPipeSeamWeldJSON>
    )

    const childPipeWrapJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.PipeWrap
    })

    const pipeWraps = GeckoPipeWrap.CreateMapFromJSON(
      id,
      childPipeWrapJSON as Record<string, GeckoPipeWrapJSON>,
      componentBounds
    )
    const nominalJSON = json.nominal
    const parsedWallNominalThickness = parseNumber(nominalJSON.wall_thickness)
    const wallNominalThickness =
      parsedWallNominalThickness !== undefined
        ? weightThicknessValue(
            GeckoMeasurement.CreateFromValue(
              parsedWallNominalThickness,
              GeckoMeasurementUnit.Meters
            )
          )
        : undefined
    const tminJson = json.tmin
    const parsedTmin = parseNumber(tminJson)
    const tmin =
      parsedTmin !== undefined
        ? weightThicknessValue(
            GeckoMeasurement.CreateFromValue(
              parsedTmin,
              GeckoMeasurementUnit.Meters
            )
          )
        : undefined

    return new GeckoPipeElbow(
      id,
      json,
      name,
      pipeRadius,
      pipeMajorRadius,
      pipeAngle,
      material,
      installationDate,
      jointEfficiency,
      stressTolerance,
      flowDirection,
      clockBearingRotation,
      componentBounds,
      sceneConfig,
      nozzles,
      fixedSensors,
      plates,
      verticalWelds,
      horizontalWelds,
      pipeNonIntegralSupports,
      pipeIntegralSupports,
      pipeCircumferentialWelds,
      pipeSeamWelds,
      pipeWraps,
      wallNominalThickness,
      tmin,
      compassOrientation
    )
  }

  public asJSON(): PipeElbowAssetModelJSON {
    return generatePipeElbow(
      this.name,
      this.radius,
      this.majorRadius,
      this.angle,
      this.material,
      this.installationDate,
      this.jointEfficiency,
      this.stressTolerance,
      this.flowDirection,
      this.clockBearingRotation,
      this.wallNominalThickness,
      this.tmin,
      this.compassOrientation,
      this.json as PipeElbowAssetModelJSON
    )
  }
}

const generatePipeElbow = (
  name: string,
  radius: GeckoMeasurement,
  majorRadius: GeckoMeasurement,
  angle: number,
  material: string | undefined,
  installationDate: Date | undefined,
  jointEfficiency: number | undefined,
  stressTolerance: GeckoMeasurement | undefined,
  flowDirection: GeckoPipeFlowDirection | undefined,
  clockBearingRotation: number | undefined,
  wallNominalThickness: GeckoMeasurement | undefined,
  tmin: GeckoMeasurement | undefined,
  compassOrientation: number | undefined,
  otherPipeElbowData: PipeElbowAssetModelJSON = NewPipeElbowAssetModelJSON
): PipeElbowAssetModelJSON => {
  const pipeRadius = radius
    .convertTo(GeckoMeasurementUnit.Meters)
    .toPrecision(assetModelPrecision).num

  const pipeMajorRadius = majorRadius
    .convertTo(GeckoMeasurementUnit.Meters)
    .toPrecision(assetModelPrecision).num

  // For a quarter torus, the circumference is pi/2 * majorRadius
  const pipeElbowCircumference = GeckoMeasurement.CreateFromValue(
    pipeRadius * 2 * pi,
    GeckoMeasurementUnit.Meters
  ).toPrecision(assetModelPrecision).num

  // For a quarter torus, the arc length is pi/2 * majorRadius
  const pipeElbowArcLength = GeckoMeasurement.CreateFromValue(
    angle * (pi / 180) * pipeMajorRadius,
    GeckoMeasurementUnit.Meters
  ).toPrecision(assetModelPrecision).num

  const wallNominalThicknessMeters =
    wallNominalThickness !== undefined
      ? wallNominalThickness
          .convertTo(GeckoMeasurementUnit.Meters)
          .toPrecision(assetModelPrecision).num
      : undefined

  const tminMeters =
    tmin !== undefined
      ? tmin
          .convertTo(GeckoMeasurementUnit.Meters)
          .toPrecision(assetModelPrecision).num
      : undefined

  const parsedInstallationDate = installationDate
    ? installationDate.toISOString().split('T')[0]
    : undefined

  const parsedStressTolerance =
    stressTolerance !== undefined
      ? stressTolerance
          .convertTo(GeckoMeasurementUnit.Megapascals)
          .toPrecision(assetModelPrecision).num
      : undefined

  return {
    name: name,
    type: GeckoComponentModelType.PipeElbow,
    radius: pipeRadius,
    major_radius: pipeMajorRadius,
    angle: angle,
    material: material,
    installation_date: parsedInstallationDate,
    joint_efficiency: jointEfficiency,
    stress_tolerance: parsedStressTolerance,
    flow_direction: flowDirection,
    clock_bearing_rotation: clockBearingRotation,
    features: otherPipeElbowData.features,
    scene_config: otherPipeElbowData.scene_config,
    component_bounds: {
      x_start: 0,
      y_start: 0,
      x_end: pipeElbowCircumference,
      y_end: pipeElbowArcLength,
    },
    tmin: tminMeters,
    compass_orientation: compassOrientation,
    nominal: { wall_thickness: wallNominalThicknessMeters },
  }
}

export const PipeElbowFormFields = (pipeElbowJSON: PipeElbowAssetModelJSON) => {
  return [
    {
      dataKey: 'name',
      label: 'Pipe Elbow Name',
      value: pipeElbowJSON.name,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'radius',
      label: 'Radius',
      value: pipeElbowJSON.radius,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'major_radius',
      label: 'Major Radius',
      value: pipeElbowJSON.major_radius,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'Angle',
      label: 'Angle',
      value: pipeElbowJSON.angle,
      unit_magnitude: null,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'material',
      label: 'Material',
      value: pipeElbowJSON.material,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    installationDateFormField(pipeElbowJSON.installation_date, true),
    {
      dataKey: 'joint_efficiency',
      label: 'Joint Efficiency',
      value: pipeElbowJSON.joint_efficiency,
      unit_magnitude: null,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: true,
      inputType: InputType.Number,
    },
    {
      dataKey: 'stress_tolerance',
      label: 'Stress Tolerance',
      value: pipeElbowJSON.stress_tolerance,
      unit_magnitude: UnitMagnitude.PsiOrMPa,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: true,
      inputType: InputType.Number,
    },
    {
      dataKey: 'flow_direction',
      label: 'Flow Direction',
      value: pipeElbowJSON.flow_direction,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      options: [
        {
          value: GeckoPipeFlowDirection.Unknown,
          label: 'Unknown',
        },
        {
          value: GeckoPipeFlowDirection.Positive,
          label: 'Positive',
        },
        {
          value: GeckoPipeFlowDirection.Negative,
          label: 'Negative',
        },
      ],
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Dropdown,
    },
    {
      dataKey: 'clock_bearing_rotation',
      label: 'Clock Rotation',
      value: pipeElbowJSON.clock_bearing_rotation,
      unit_magnitude: null,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    wallThicknessFormField(pipeElbowJSON.nominal.wall_thickness),
    tminFormField(pipeElbowJSON.tmin),
    compassOrientationFormField(pipeElbowJSON.compass_orientation),
  ]
}

export const PipeElbowJSONFromFormFields = (
  component: PipeElbowAssetModelJSON,
  formFields: FormField[]
) => {
  const parsedJointEfficiency = parseNumber(
    getFormFieldByDataKey(formFields, 'joint_efficiency')?.value
  )
  const parsedStressTolerance = parseNumber(
    getFormFieldByDataKey(formFields, 'stress_tolerance')?.value
  )
  const parsedWallNominalThickness = parseNumber(
    getFormFieldByDataKey(formFields, 'wall_thickness')?.value
  )
  const parsedTmin = parseNumber(
    getFormFieldByDataKey(formFields, 'tmin')?.value
  )
  const installationDate = getFormFieldByDataKey(
    formFields,
    'installation_date'
  )
  const flowDirection = getFormFieldByDataKey(formFields, 'flow_direction')
  const compassOrientation = getNumberFieldByDataKey(
    formFields,
    'compass_orientation'
  )
  return generatePipeElbow(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'radius')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'major_radius')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    getFormFieldByDataKey(formFields, 'Angle')?.value as number,
    parseString(getFormFieldByDataKey(formFields, 'material')?.value),
    installationDate?.value
      ? new Date(installationDate?.value as string)
      : undefined,
    parsedJointEfficiency,
    parsedStressTolerance !== undefined
      ? GeckoMeasurement.CreateFromValue(
          parsedStressTolerance,
          GeckoMeasurementUnit.Megapascals
        )
      : undefined,
    flowDirection?.value === 'undefined'
      ? undefined
      : (flowDirection?.value as GeckoPipeFlowDirection),
    parseNumber(
      getFormFieldByDataKey(formFields, 'clock_bearing_rotation')?.value
    ),
    parsedWallNominalThickness !== undefined
      ? GeckoMeasurement.CreateFromValue(
          parsedWallNominalThickness,
          GeckoMeasurementUnit.Meters
        )
      : undefined,
    parsedTmin !== undefined
      ? GeckoMeasurement.CreateFromValue(
          parsedTmin,
          GeckoMeasurementUnit.Meters
        )
      : undefined,
    compassOrientation,
    component
  )
}
